import { useRef, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import MuiPrintIcon from '@mui/icons-material/Print';
import ListItemText from '@mui/material/ListItemText';
import MuiSearchIcon from '@mui/icons-material/Search';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ListItemButton from '@mui/material/ListItemButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import MuiErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { usePrinterStore } from 'src/store/PrinterStore';
import { getPlatformPrinter } from 'src/platform/services';
import { toSavedPrinter, type StarPrinter, type SavedPrinter } from 'src/services/printers';

import MuiIcon from 'src/components/mui-icon';

import LanPrinterDialog from './lan/lan-printer-dialog';
import { usesDesktopLanSetup } from './lan/desktop-lan';
import { usePrinterDiscovery } from './use-printer-discovery';
import EscPosPrinterDialog from './escpos/escpos-printer-dialog';
import { issueLabel, interfaceIcon } from './printer-interface-meta';

// ----------------------------------------------------------------------

interface PrinterListDialogProps {
  open: boolean;
  onClose: () => void;
  /** The chosen printer, already in its persisted shape (Star or ESC/POS). */
  onSelected: (printer: SavedPrinter) => void;
  currentDefaultId?: string | null;
  isCurrent?: () => boolean;
  silent?: boolean;
}

/**
 * What this dialog offers is a **capability** question (SITE_TRIAGE bucket B1),
 * answered here rather than taken as a prop — the printing service's
 * prompt-for-printer flow renders this same dialog, and a cashier who hits that
 * prompt mid-sale on a desktop register must get the same options as in
 * Settings.
 *
 * - A desktop register (Star LAN backend) gets `LanPrinterDialog`: automatic
 *   discovery **or** add by IP address, then check → test page → save. There
 *   is no pairing on LAN, so the desktop copy says Add / Registered printer.
 * - When the register offers generic ESC/POS printing **and** the operator
 *   chose that method in Settings, the same entry points open
 *   `EscPosPrinterDialog` instead. The method decides the setup flow only;
 *   every saved printer keeps its own driver.
 * - Every other runtime gets the SDK-driven list below, unchanged.
 */
const printerCapabilities = getPlatformPrinter();
const lanOnly = printerCapabilities.supportedInterfaces.every(
  (interfaceType) => interfaceType === 'lan'
);
const desktopLan = usesDesktopLanSetup(printerCapabilities.thermalPrinting);
const escPosAvailable = printerCapabilities.escPosLanPrinting;

export default function PrinterListDialog(props: PrinterListDialogProps) {
  const { open, onClose, onSelected, currentDefaultId } = props;
  const method = usePrinterStore((state) => state.printerMethod);
  if (escPosAvailable && method === 'escpos') {
    return (
      <EscPosPrinterDialog
        open={open}
        onClose={onClose}
        currentDefaultId={currentDefaultId}
        // The prompt-for-printer flow wants an active printer; "Save" alone
        // remembers it and the caller decides what to do with the selection.
        onSaved={(printer, use) => {
          if (use) onSelected(printer);
          else usePrinterStore.getState().savePrinter(printer);
        }}
      />
    );
  }
  // A module constant, so the same branch renders for the component's whole life.
  return desktopLan ? <LanPrinterDialog {...props} /> : <SdkPrinterListDialog {...props} />;
}

function SdkPrinterListDialog({
  open,
  onClose,
  onSelected,
  currentDefaultId,
  isCurrent,
  silent,
}: PrinterListDialogProps) {
  const active = useRef(true);
  const current = useCallback(() => active.current && (!isCurrent || isCurrent()), [isCurrent]);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const {
    printers,
    issues,
    loading,
    error,
    includeBle,
    includeUsb,
    setIncludeBle,
    setIncludeUsb,
    connectingId,
    discover,
    connect,
  } = usePrinterDiscovery({ isCurrent: isCurrent ? current : undefined, silent });

  // Auto-discover when the dialog opens.
  useEffect(() => {
    if (open) discover();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSelect = async (printer: StarPrinter) => {
    if ((await connect(printer)) && (!isCurrent || current())) {
      onSelected(toSavedPrinter(printer));
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <MuiIcon icon={MuiPrintIcon} />
          </Avatar>
          <Box>
            <Typography variant="h6">Select Printer</Typography>
            <Typography variant="caption" color="text.secondary">
              Discover and choose your default Star printer
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {silent && error && <Alert severity="error" sx={{ mx: 3, mb: 2 }}>{error}</Alert>}
        <Box
          sx={{
            px: 3,
            pb: 1,
            gap: 1,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
            {!lanOnly && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={includeBle}
                      onChange={(e) => setIncludeBle(e.target.checked)}
                    />
                  }
                  label="Include BLE"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={includeUsb}
                      onChange={(e) => setIncludeUsb(e.target.checked)}
                    />
                  }
                  label="Include USB"
                />
              </>
            )}
          </Stack>
          <LoadingButton
            size="small"
            variant="outlined"
            loading={loading}
            onClick={discover}
            startIcon={<MuiIcon icon={MuiSearchIcon} />}
          >
            Discover
          </LoadingButton>
        </Box>

        {/* Per-interface issues (e.g. an interface skipped due to permissions). */}
        {!loading && issues.length > 0 && (
          <Stack spacing={1} sx={{ px: 3, pb: 1 }}>
            {issues.map((issue, i) => (
              <Alert key={`${issue.code}-${i}`} severity="warning" sx={{ py: 0.25 }}>
                <strong>{issueLabel(issue.interfaceType)}:</strong> {issue.message}
              </Alert>
            ))}
          </Stack>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && printers.length > 0 && (
          <List sx={{ p: 0 }}>
            {printers.map((printer) => (
              <ListItem key={printer.identifier} sx={{ p: 0 }}>
                <ListItemButton
                  onClick={() => handleSelect(printer)}
                  disabled={connectingId !== null}
                  selected={printer.identifier === currentDefaultId}
                  sx={{
                    p: 2,
                    '&.Mui-selected': { bgcolor: 'primary.lighter' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                      <MuiIcon icon={interfaceIcon(printer.interfaceType)} width={24} />
                    </Avatar>

                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {printer.name ?? printer.modelName ?? printer.model}
                          </Typography>
                          {printer.identifier === currentDefaultId && (
                            <Chip label="Default" size="small" color="primary" variant="filled" />
                          )}
                          {printer.isMock && (
                            <Chip label="Mock" size="small" color="warning" variant="outlined" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {printer.model} · {printer.interfaceType.toUpperCase()}
                          {printer.ipAddress ? ` · ${printer.ipAddress}` : ''}
                          {printer.macAddress ? ` · ${printer.macAddress}` : ''}
                        </Typography>
                      }
                    />

                    {connectingId === printer.identifier && <CircularProgress size={20} />}
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {!loading && printers.length === 0 && (
          <Card sx={{ m: 2, p: 3, textAlign: 'center' }}>
            <MuiIcon icon={MuiErrorOutlineIcon} width={48} sx={{ color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
              No Printers Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lanOnly
                ? 'Make sure the printer is powered on and on the same network, then discover again.'
                : "Make sure the printer is powered on and on the same network, then discover again. Bluetooth printers must be paired in the device's Settings first; enable \u201cInclude BLE\u201d for Bluetooth-LE models."}
            </Typography>
          </Card>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
